import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, homeFor, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Quiosco from "@/pages/Quiosco";
import ValidarJornadaWhatsApp from "@/pages/ValidarJornadaWhatsApp";
import EjemploFichajeWhatsApp from "@/pages/EjemploFichajeWhatsApp";
import WorkerLayout from "@/components/layouts/WorkerLayout";
import AdminLayout from "@/components/layouts/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Fichar from "@/pages/worker/Fichar";
import MisFichajes from "@/pages/worker/MisFichajes";
import MiCuadro from "@/pages/worker/MiCuadro";
import MisZonas from "@/pages/worker/MisZonas";
import MisSolicitudes from "@/pages/worker/MisSolicitudes";
import MisNotificaciones from "@/pages/worker/MisNotificaciones";
import MiPerfil from "@/pages/worker/MiPerfil";
import Dashboard from "@/pages/admin/Dashboard";
import Empleados from "@/pages/admin/Empleados";
import FichaEmpleado from "@/pages/admin/FichaEmpleado";
import FichajesAdmin from "@/pages/admin/FichajesAdmin";
import Solicitudes from "@/pages/admin/Solicitudes";
import Comunicados from "@/pages/admin/Comunicados";
import Informes from "@/pages/admin/Informes";
import Planificacion from "@/pages/admin/Planificacion";
import VacacionesCalendario from "@/pages/admin/VacacionesCalendario";
import Organizacion from "@/pages/admin/Organizacion";
import Turnos from "@/pages/admin/Turnos";
import ZonasPlanificacion from "@/pages/admin/ZonasPlanificacion";
import AnalisisPausas from "@/pages/admin/AnalisisPausas";
import Ajustes from "@/pages/admin/Ajustes";
import Documentos from "@/pages/admin/Documentos";
import Proyectos from "@/pages/admin/Proyectos";
import MisDocumentos from "@/pages/worker/MisDocumentos";
import MisProyectos from "@/pages/worker/MisProyectos";
import MisGastos from "@/pages/worker/MisGastos";
import MisRutas from "@/pages/worker/MisRutas";
import MisFestivos from "@/pages/worker/MisFestivos";
import MiCalendario from "@/pages/worker/MiCalendario";
import CalendarioPicados from "@/pages/worker/CalendarioPicados";
import Gastos from "@/pages/admin/Gastos";
import RutasComerciales from "@/pages/admin/RutasComerciales";
import Empresas from "@/pages/superadmin/Empresas";
import PlantillasColores from "@/pages/superadmin/PlantillasColores";
import GrupoGerencia from "@/pages/admin/GrupoGerencia";
import ConfiguracionSistema from "@/pages/superadmin/Configuracion";
import ApkAndroid from "@/pages/superadmin/ApkAndroid";
import EmpresaThemeProvider from "@/components/EmpresaThemeProvider";

function Cargando() {
  return (
    <div className="flex h-screen items-center justify-center bg-white dark:bg-zinc-950">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-950 dark:border-zinc-700 dark:border-t-zinc-100" />
    </div>
  );
}

function Protected({ roles, children, allowGerencia }) {
  const { user } = useAuth();
  if (user === null) return <Cargando />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.rol)) {
    if (!(allowGerencia && (user.es_aprobador_gerencia || user.es_miembro_gerencia))) {
      return <Navigate to={homeFor(user)} replace />;
    }
  }
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <Cargando />;
  return <Navigate to={homeFor(user || undefined)} replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="control-horario-theme">
      <AuthProvider>
        <EmpresaThemeProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/quiosco/:token" element={<Quiosco />} />
          <Route path="/wa/ejemplo" element={<EjemploFichajeWhatsApp />} />
          <Route path="/wa/jornada/:token" element={<ValidarJornadaWhatsApp />} />
          <Route path="/" element={<HomeRedirect />} />

          <Route
            element={
              <Protected roles={["worker", "manager", "admin"]}>
                <WorkerLayout />
              </Protected>
            }
          >
            <Route path="/fichar" element={<Fichar />} />
            <Route path="/mis-fichajes" element={<MisFichajes />} />
            <Route path="/calendario-picados" element={<CalendarioPicados />} />
            <Route path="/mis-solicitudes" element={<MisSolicitudes />} />
            <Route path="/mis-notificaciones" element={<MisNotificaciones />} />
            <Route path="/mi-cuadro" element={<MiCuadro />} />
            <Route path="/mis-zonas" element={<MisZonas />} />
            <Route path="/mis-informes" element={<Informes />} />
            <Route path="/mis-documentos" element={<ErrorBoundary><MisDocumentos /></ErrorBoundary>} />
            <Route path="/mis-proyectos" element={<ErrorBoundary><MisProyectos /></ErrorBoundary>} />
            <Route path="/mis-gastos" element={<ErrorBoundary><MisGastos /></ErrorBoundary>} />
            <Route path="/mis-rutas" element={<ErrorBoundary><MisRutas /></ErrorBoundary>} />
            <Route path="/mis-festivos" element={<MisFestivos />} />
            <Route path="/mi-calendario" element={<MiCalendario />} />
            <Route path="/mi-perfil" element={<MiPerfil />} />
          </Route>

          <Route
            element={
              <Protected roles={["admin", "manager"]} allowGerencia>
                <AdminLayout />
              </Protected>
            }
          >
            <Route path="/admin" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
            <Route path="/admin/empleados" element={<Empleados />} />
            <Route path="/admin/empleados/:id" element={<FichaEmpleado />} />
            <Route path="/admin/departamentos" element={<Navigate to="/admin/organizacion" replace />} />
            <Route path="/admin/fichajes" element={<FichajesAdmin />} />
            <Route path="/admin/solicitudes" element={<Solicitudes />} />
            <Route path="/admin/comunicados" element={<Comunicados />} />
            <Route path="/admin/informes" element={<Informes />} />
            <Route path="/admin/documentos" element={<ErrorBoundary><Documentos /></ErrorBoundary>} />
            <Route path="/admin/proyectos" element={<ErrorBoundary><Proyectos /></ErrorBoundary>} />
            <Route path="/admin/gastos" element={<ErrorBoundary><Gastos /></ErrorBoundary>} />
            <Route path="/admin/rutas-comerciales" element={<ErrorBoundary><RutasComerciales /></ErrorBoundary>} />
            <Route path="/admin/analisis-pausas" element={<AnalisisPausas />} />
            <Route path="/admin/planificacion" element={<Planificacion />} />
            <Route path="/admin/vacaciones" element={<VacacionesCalendario />} />
            <Route path="/admin/grupo-gerencia" element={<ErrorBoundary><GrupoGerencia /></ErrorBoundary>} />
            <Route path="/admin/organizacion" element={<Organizacion />} />
            <Route path="/admin/equipo" element={<Organizacion />} />
            <Route path="/admin/turnos" element={<Turnos />} />
            <Route path="/admin/zonas" element={<ZonasPlanificacion />} />
            <Route path="/admin/ajustes" element={<ErrorBoundary><Ajustes /></ErrorBoundary>} />
          </Route>

          <Route
            element={
              <Protected roles={["super_admin"]}>
                <AdminLayout />
              </Protected>
            }
          >
            <Route path="/superadmin/empresas" element={<Empresas />} />
            <Route path="/superadmin/plantillas-colores" element={<PlantillasColores />} />
            <Route path="/superadmin/configuracion" element={<ConfiguracionSistema />} />
            <Route path="/superadmin/apk" element={<ApkAndroid />} />
          </Route>

          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
        </EmpresaThemeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
